/* Circadia client event catalog — the S3 web-tracking harness.
   ============================================================================
   Client-Web Analytics (S3). Fires the v1 commerce-browsing events through the
   single keepalive beacon (window.Circadia.analytics.emit) defined in
   analytics-emit.js. search_performed already fires from search-everything.js.

   Catalog (all 'analytics'-gated server-side):
     page_viewed        — once per page load (every page)
     product_viewed     — product pages (from analyticsContext.product)
     collection_viewed  — collection pages
     cart_viewed        — cart page
     cart_item_added    — Shopify /cart/add.js (fetch interception)
     cart_item_removed  — Shopify /cart/change.js with quantity 0 (a line removal)
     checkout_started   — click on a checkout control

   ── THE HARD GATE ───────────────────────────────────────────────────────────
   NOTHING here fires before analytics consent. Every emit goes through the
   harness, which itself gates on window.Circadia.consent; this file ALSO checks
   granted() at each fire point as defense-in-depth. On consent being granted
   later (the banner dispatches 'circadia:consent-changed'), the deferred
   page_viewed fires then.

   ── BEACON, NOT BATCH (auto-decided, flagged) ───────────────────────────────
   We reuse the ONE established keepalive beacon per event (emit) rather than a
   multi-event batch endpoint. v1 fires each catalog event AT MOST ONCE per page
   / per user action (page_viewed is deduped), so this is not a per-event flood.
   A true client-side batch queue + a /proxy/events endpoint is a clean follow-on
   if event volume grows. */

(function () {
  'use strict';

  function ctx() {
    return (window.Circadia && window.Circadia.analyticsContext) || {};
  }

  function granted() {
    try {
      return !!(
        window.Circadia &&
        window.Circadia.analytics &&
        typeof window.Circadia.analytics.analyticsGranted === 'function' &&
        window.Circadia.analytics.analyticsGranted()
      );
    } catch (_) {
      return false;
    }
  }

  function emit(name, props) {
    try {
      if (
        window.Circadia &&
        window.Circadia.analytics &&
        typeof window.Circadia.analytics.emit === 'function'
      ) {
        window.Circadia.analytics.emit(name, props || {});
      }
    } catch (_) {
      /* the harness already swallows; belt-and-suspenders */
    }
  }

  // ── page_viewed + the page-type event (once per load) ─────────────────────
  var pageViewFired = false;
  function firePageView() {
    if (pageViewFired) return;
    if (!granted()) return; // gate: nothing pre-consent (re-tried on consent grant)
    pageViewFired = true;

    var c = ctx();
    emit('page_viewed', { page_type: c.page_type || null, template: c.template || null });

    if (c.page_type === 'product' && c.product) {
      emit('product_viewed', {
        product_id: c.product.id,
        handle: c.product.handle,
        title: c.product.title,
        type: c.product.type,
        price: c.product.price,
      });
    } else if (c.page_type === 'collection' && c.collection) {
      emit('collection_viewed', {
        collection_id: c.collection.id,
        handle: c.collection.handle,
        title: c.collection.title,
        products_count: c.collection.products_count,
      });
    } else if (c.page_type === 'cart') {
      emit('cart_viewed', {
        item_count: c.cart ? c.cart.item_count : null,
        total_price: c.cart ? c.cart.total_price : null,
      });
    }
  }

  // ── cart add / remove via Shopify AJAX interception (theme-agnostic) ───────
  function bodyToObject(init) {
    try {
      if (!init || !init.body) return {};
      if (typeof init.body === 'string') {
        var t = init.body.trim();
        if (t.charAt(0) === '{') return JSON.parse(t);
        var out = {};
        new URLSearchParams(t).forEach(function (v, k) {
          out[k] = v;
        });
        return out;
      }
      if (typeof FormData !== 'undefined' && init.body instanceof FormData) {
        var o = {};
        init.body.forEach(function (v, k) {
          o[k] = v;
        });
        return o;
      }
    } catch (_) {
      /* unparseable body → empty props */
    }
    return {};
  }

  function hookCart() {
    if (!window.fetch || window.fetch.__circadiaWrapped) return;
    var orig = window.fetch;
    function wrapped(input) {
      var url = '';
      try {
        url = typeof input === 'string' ? input : (input && input.url) || '';
      } catch (_) {
        url = '';
      }
      var init = arguments.length > 1 ? arguments[1] : undefined;
      var promise = orig.apply(this, arguments);
      try {
        if (granted() && url) {
          if (/\/cart\/add(\.js)?(\?|$)/.test(url)) {
            var addBody = bodyToObject(init);
            if (Array.isArray(addBody.items)) {
              // Dawn quick-add / bundle: {items:[{id, quantity}, ...]} → one event per line.
              addBody.items.forEach(function (it) {
                if (!it) return;
                emit('cart_item_added', {
                  variant_id: it.id != null ? it.id : null,
                  quantity: it.quantity ? Number(it.quantity) : 1,
                });
              });
            } else {
              emit('cart_item_added', {
                variant_id: addBody.id || null,
                quantity: addBody.quantity ? Number(addBody.quantity) : 1,
              });
            }
          } else if (/\/cart\/(change|update)(\.js)?(\?|$)/.test(url)) {
            var chBody = bodyToObject(init);
            // Only a true line removal (quantity 0) counts as cart_item_removed.
            if (String(chBody.quantity) === '0') {
              emit('cart_item_removed', { line: chBody.line || null, variant_id: chBody.id || null });
            }
          }
        }
      } catch (_) {
        /* never break the cart fetch */
      }
      return promise;
    }
    wrapped.__circadiaWrapped = true;
    window.fetch = wrapped;
  }

  // ── checkout_started ──────────────────────────────────────────────────────
  // One-shot per page load (mirrors pageViewFired): repeated clicks on a checkout
  // control must not re-fire the event.
  var checkoutStartedFired = false;
  function isCheckoutControl(node) {
    if (!node || !node.matches) return false;
    return node.matches(
      'button[name="checkout"], input[name="checkout"], [data-checkout], a[href*="/checkout"]'
    );
  }
  function hookCheckout() {
    document.addEventListener(
      'click',
      function (e) {
        try {
          if (!granted()) return;
          var node = e.target;
          while (node && node !== document.body) {
            if (isCheckoutControl(node)) {
              if (checkoutStartedFired) return;
              checkoutStartedFired = true;
              var c = ctx();
              emit('checkout_started', {
                item_count: c.cart ? c.cart.item_count : null,
                total_price: c.cart ? c.cart.total_price : null,
              });
              return;
            }
            node = node.parentNode;
          }
        } catch (_) {
          /* never break a checkout click */
        }
      },
      true
    );
  }

  function init() {
    hookCart();
    hookCheckout();
    firePageView();
    // If consent is granted later (banner accept), fire the deferred page view.
    window.addEventListener('circadia:consent-changed', function () {
      firePageView();
    });
  }

  if (document.readyState !== 'loading') init();
  else document.addEventListener('DOMContentLoaded', init);
})();
